import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

@Directive({
    selector: 'input[trim],textarea[trim]',
    standalone: true,
})
export class TrimDirective implements OnInit, OnDestroy {
    private elementRef = inject(ElementRef);
    private ngControl = inject(NgControl, { optional: true });

    @Input() trim: TrimOption = '';
    @Input() trimOnWriteValue = true;
    @Input() trimOnInput = false;

    private _valueAccessor: ControlValueAccessor | null = null;
    private _writeValue: ((value: any) => void) | null = null;

    private static getCaret(el: TrimElement) {
        return {
            start: el.selectionStart,
            end: el.selectionEnd,
        };
    }

    private static setCaret(el: TrimElement, start: number | null, end: number | null) {
        el.selectionStart = start;
        el.selectionEnd = end;
        if (document.activeElement !== el) {
            el.focus();
        }
    }

    private static dispatchEvent(el: TrimElement, eventType: string) {
        const event = new Event(eventType);
        el.dispatchEvent(event);
    }

    private static trimValue(el: TrimElement, value: string) {
        el.value = value.trim();
        TrimDirective.dispatchEvent(el, 'input');
    }

    ngOnInit(): void {
        if (!this.ngControl) {
            console.warn('NgxTrimDirective should be used with a form control.');
            return;
        }

        this._valueAccessor = this.ngControl.valueAccessor;

        if (this._valueAccessor) {
            this._writeValue = this._valueAccessor.writeValue;
            this._valueAccessor.writeValue = (value: any) => {
                const trimmedValue = this.shouldTrimOnWrite(value) ? value.trim() : value;
                this._writeValue?.call(this._valueAccessor, trimmedValue);
                if (value !== trimmedValue) {
                    this.triggerOnChange(trimmedValue);
                }
            };
        }
    }

    ngOnDestroy(): void {
        if (this._valueAccessor && this._writeValue) {
            this._valueAccessor.writeValue = this._writeValue;
        }
    }

    private shouldTrimOnWrite(value: any): boolean {
        return (
            this.trim !== false &&
            typeof value === 'string' &&
            typeof value.trim === 'function' &&
            this.trimOnWriteValue
        );
    }

    private triggerOnChange(value: string): void {
        (this._valueAccessor as any)?.['onChange']?.(value);
        (this._valueAccessor as any)?.['onTouched']?.();
    }

    @HostListener('blur', ['$event.target', '$event.target.value'])
    onBlur(el: TrimElement, value: string): void {
        if (this.trim === 'blur' && value.trim() !== value) {
            const { start, end } = TrimDirective.getCaret(el);
            TrimDirective.trimValue(el, value);
            TrimDirective.setCaret(el, start, end);
        }
    }

    @HostListener('input', ['$event.target', '$event.target.value'])
    onInput(el: TrimElement, value: string): void {
        if (!this.trimOnInput) {
            return;
        }
        if (this.trim === '' && value.trim() !== value) {
            const { start, end } = TrimDirective.getCaret(el);
            TrimDirective.trimValue(el, value);
            TrimDirective.setCaret(el, start, end);
        }
    }
}

type TrimElement = HTMLInputElement | HTMLTextAreaElement;
export type TrimOption = '' | 'blur' | false;
